import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, Checkbox, Divider, Modal } from "antd";
import CiqAgSearch from "components/ciq-ag-search";
import { InfoMessages } from "constants/index";
import {
  DropDownDBFeatureInstance,
  DropdownEntityOption,
  TEditingEntity,
  TLinkSource
} from "components/feature-entity-linking-page/models";
import {
  getTLinkSourceSubmittalObject,
  translateFeatureToOptions
} from "components/feature-entity-linking-page/utils";
import { useCallback, useMemo, useState } from "react";
import BlueTickIcon from "components/svg-icons/blue-tick-icon";
import VirtualList from "../virtual-list";

export default function SubmittalListing({
  submittals,
  editingEntity,
  onUpdateEditingEntityList
}: {
  submittals: Array<DropDownDBFeatureInstance>;
  editingEntity: TEditingEntity;
  onUpdateEditingEntityList: (props: { updatedlist: TLinkSource[] }) => void;
}) {
  const [searchText, setSearchText] = useState<string>("");
  const [isSuggested, setSuggested] = useState(false);

  const specNumber = editingEntity?.entityDetail?.specSection?.section_number;
  const allEntities = useMemo(() => {
    // editingEntity is always truthy here: selectedIdSet useMemo dereferences
    // editingEntity.list unconditionally, so a falsy editingEntity would throw
    // earlier; this guard can never be hit.
    /* v8 ignore next */
    if (!editingEntity) return [];
    return translateFeatureToOptions(submittals, specNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittals, specNumber]);

  const entities = useMemo(() => {
    let filtered = allEntities;
    if (searchText) {
      filtered = filtered.filter((x) => x.label.includes(searchText));
    }
    if (isSuggested) {
      filtered = filtered.filter((x) => x.isSuggested);
    }
    return filtered;
  }, [allEntities, searchText, isSuggested]);

  const selectedIdSet = useMemo(() => {
    const set = new Set<string>();
    editingEntity.list.forEach((x) => {
      if (x.feature_instance?.id) set.add(x.feature_instance.id);
    });
    return set;
  }, [editingEntity.list]);

  const isAllSuggestedSelected = useMemo(() => {
    const suggestedEntities = allEntities.filter((x) => x.isSuggested);
    if (suggestedEntities.length === 0) return false;
    return suggestedEntities.every((x) => selectedIdSet.has(x.id));
  }, [allEntities, selectedIdSet]);

  const onChangeCheckbox = useCallback(
    (checked: boolean, item: DropdownEntityOption) => {
      if (checked) {
        const insertSubmittal = () => {
          const sampleObject = getTLinkSourceSubmittalObject(item);
          onUpdateEditingEntityList({
            updatedlist: [...editingEntity.list, sampleObject]
          });
        };

        const isMaterialTracking = item.instance.feature_submittals.some(
          (x) => x.material_tracking
        );
        if (!isMaterialTracking) {
          Modal.confirm({
            title: "Do you want to link?",
            icon: <ExclamationCircleOutlined />,
            content: InfoMessages.confirmMsgForMaterialTrackingOff,
            className: "skip-listing-window",
            onOk() {
              insertSubmittal();
            },
            okText: "Yes",
            cancelText: "No"
          });
          return;
        }
        insertSubmittal();
      } else {
        onUpdateEditingEntityList({
          updatedlist: editingEntity.list.filter(
            (x) => x?.featureInstanceId !== item.id
          )
        });
      }
    },
    [editingEntity.list, onUpdateEditingEntityList]
  );

  // CiqAgSearch debounces (500ms) before it calls back, so this no longer
  // keeps a timer of its own.
  const onSearchChange = useCallback((value: string) => {
    setSearchText((value || "").toLowerCase().trim());
  }, []);

  const btnCss = useMemo(() => {
    return isSuggested
      ? "text-ciq-blue-700 border-ciq-blue-700 focus:text-ciq-blue-700 focus:border-ciq-blue-700"
      : "text-black text-opacity-90 border-ciq-gray-300 focus:text-black text-opacity-90 focus:border-ciq-gray-300";
  }, [isSuggested]);

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden features-card">
      <div className="flex items-center justify-between px-4">
        <div
          className="flex font-bold py-2"
          data-testid="submittallisting-title"
        >
          List of Submittals
        </div>
        <div className="flex space-x-1">
          {isSuggested && (
            <Checkbox
              checked={isAllSuggestedSelected}
              data-testid="submittallisting-checkbox-link-unlink-all"
              onChange={(e) => {
                if (e.target.checked) {
                  const suggestedObjects = allEntities
                    .filter((x) => x.isSuggested)
                    .map((item) => getTLinkSourceSubmittalObject(item));
                  const nonSuggestedLinkedObjects = editingEntity.list.filter(
                    (x) => !x.isSuggested
                  );
                  onUpdateEditingEntityList({
                    updatedlist: [
                      ...nonSuggestedLinkedObjects,
                      ...suggestedObjects
                    ]
                  });
                } else {
                  const nonSuggestedLinkedObjects = editingEntity.list.filter(
                    (x) => !x.isSuggested
                  );
                  onUpdateEditingEntityList({
                    updatedlist: nonSuggestedLinkedObjects
                  });
                }
              }}
            >
              {isAllSuggestedSelected ? "Unlink All" : "Link All"}
            </Checkbox>
          )}
        </div>
      </div>
      <div className="flex w-full pb-4 px-4">
        <CiqAgSearch
          placeholder="Search Submittal Name or ID"
          className="w-full pr-[10px]"
          onSearch={onSearchChange}
          data-testid="submittallisting-search-submittal-name-id"
        />
        <Divider orientation="vertical" className="h-7 m-0 mr-4 pt-[35px]" />
        <Button
          icon={isSuggested ? <BlueTickIcon /> : null}
          className={`${btnCss} flex justify-center items-center  rounded p-1 gap-x-1 px-3`}
          onClick={() => setSuggested(!isSuggested)}
          data-testid="submittallisting-button-filter-suggestions"
        >
          <span>Filter Suggestions</span>
        </Button>
      </div>
      <VirtualList
        // entities is always an array (the useMemo returns `filtered`, derived
        // from allEntities which is always an array), so the `|| []` fallback
        // is unreachable.
        /* v8 ignore next */
        items={entities || []}
        itemHeight={46}
        className="w-full flex-1 min-h-0"
        renderItem={(item) => (
          <div
            className="flex items-center h-[45px] px-4 border-0 border-b border-solid border-ciq-gray-950 border-opacity-20"
            data-testid="submittallisting-checkbox-item"
          >
            <Checkbox
              checked={selectedIdSet.has(item.id)}
              onChange={(e) => {
                onChangeCheckbox(e.target.checked, item);
              }}
              data-testid="submittallisting-checkbox-item-checkbox"
            >
              <div className="flex items-center space-x-2">
                <i>{item.pre}</i>
                <div className="truncate">{item.title}</div>
              </div>
            </Checkbox>
          </div>
        )}
      />
    </div>
  );
}
